import { z } from 'zod';

/**
 * Wave 31 — Ficha de Verificação de Serviço (FVS).
 *
 * Proactive half of the quality module. Each template is a named
 * checklist ("Concretagem de pilares", "Alvenaria", "Revestimento
 * cerâmico"). An inspection instantiates a template on a project /
 * stage, the inspector fills each item conforme / não conforme / N/A
 * with optional notes, submits (status → preenchida), and a second
 * party verifies (status → verificada).
 *
 * Status lifecycle
 * ────────────────
 *   aberta     → editable; inspector is filling items
 *   preenchida → inspector signed; responses locked; awaits verifier
 *   verificada → verifier signed; FVS closed
 *
 * If any item is `nao_conforme` at verification time, the inspection
 * carries `hasNonconformity=true` — the web surface can offer to spawn
 * an RNC from those items.
 */

// ─────────────────────────────── primitives ──────────────────────────

export const fvsResponseValueSchema = z.enum([
  'conforme',
  'nao_conforme',
  'na',
]);
export type FvsResponseValue = z.infer<typeof fvsResponseValueSchema>;

export const fvsStatusSchema = z.enum([
  'aberta',
  'preenchida',
  'verificada',
]);
export type FvsStatus = z.infer<typeof fvsStatusSchema>;

/**
 * Template checklist item. `id` is a client-assigned stable identifier
 * (a short string — the web form generates it). `label` is mandatory;
 * `description` is the helper sub-line.
 */
export const fvsTemplateItemSchema = z.object({
  id: z.string().trim().min(1).max(64),
  label: z.string().trim().min(1).max(240),
  description: z.string().trim().max(600).optional().nullable(),
});
export type FvsTemplateItem = z.infer<typeof fvsTemplateItemSchema>;

/**
 * Per-item response on an inspection. `itemId` points at the snapshot
 * item (not the live template), keeping inspections immutable even if
 * the source template mutates.
 */
export const fvsResponseEntrySchema = z.object({
  itemId: z.string().trim().min(1).max(64),
  response: fvsResponseValueSchema,
  note: z.string().trim().max(600).optional().nullable(),
});
export type FvsResponseEntry = z.infer<typeof fvsResponseEntrySchema>;

// ─────────────────────────────── templates ───────────────────────────

export const fvsTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  items: z.array(fvsTemplateItemSchema),
  createdBy: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FvsTemplate = z.infer<typeof fvsTemplateSchema>;

export const createFvsTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(600).optional().nullable(),
  items: z.array(fvsTemplateItemSchema).min(1, 'Adicione ao menos um item'),
});
export type CreateFvsTemplateRequest = z.infer<typeof createFvsTemplateRequestSchema>;

export const updateFvsTemplateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(160).optional(),
    description: z.string().trim().max(600).nullable().optional(),
    items: z.array(fvsTemplateItemSchema).min(1).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdateFvsTemplateRequest = z.infer<typeof updateFvsTemplateRequestSchema>;

export const listFvsTemplatesResponseSchema = z.object({
  items: z.array(fvsTemplateSchema),
});
export type ListFvsTemplatesResponse = z.infer<typeof listFvsTemplatesResponseSchema>;

// ─────────────────────────────── inspections ─────────────────────────

export const fvsInspectionSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  stageId: z.string().nullable(),
  stageName: z.string().nullable(),
  templateId: z.string().nullable(),
  templateName: z.string(),
  itemsSnapshot: z.array(fvsTemplateItemSchema),
  responses: z.array(fvsResponseEntrySchema),
  status: fvsStatusSchema,
  hasNonconformity: z.boolean(),
  inspectorUserId: z.string().nullable(),
  inspectorName: z.string().nullable(),
  inspectorSignedAt: z.string().nullable(),
  verifierUserId: z.string().nullable(),
  verifierName: z.string().nullable(),
  verifierSignedAt: z.string().nullable(),
  scheduledDate: z.string().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FvsInspection = z.infer<typeof fvsInspectionSchema>;

export const createFvsInspectionRequestSchema = z.object({
  // Optional: either pull from a template, or pass the snapshot directly.
  templateId: z.string().optional().nullable(),
  // When `templateId` is provided the server snapshots the template. If
  // omitted, `templateName` and `itemsSnapshot` are required.
  templateName: z.string().trim().min(1).max(160).optional(),
  itemsSnapshot: z.array(fvsTemplateItemSchema).optional(),
  stageId: z.string().optional().nullable(),
  scheduledDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .optional()
    .nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
});
export type CreateFvsInspectionRequest = z.infer<typeof createFvsInspectionRequestSchema>;

export const updateFvsInspectionRequestSchema = z
  .object({
    // Responses can be patched while the inspection is `aberta`. Trying
    // to mutate responses on a locked inspection is rejected at the
    // service layer with a 409, not a schema error.
    responses: z.array(fvsResponseEntrySchema).optional(),
    status: fvsStatusSchema.optional(),
    stageId: z.string().nullable().optional(),
    scheduledDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
      .nullable()
      .optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdateFvsInspectionRequest = z.infer<typeof updateFvsInspectionRequestSchema>;

// ─────────────────────────────── summary ─────────────────────────────

export const fvsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.object({
    aberta: z.number().int().nonnegative(),
    preenchida: z.number().int().nonnegative(),
    verificada: z.number().int().nonnegative(),
  }),
  /** Verified inspections that carry at least one nao_conforme. */
  nonconforming: z.number().int().nonnegative(),
  /** Conformance %: verified-with-no-nonconformity / total verified (0 if none verified). */
  conformancePercent: z.number(),
});
export type FvsSummary = z.infer<typeof fvsSummarySchema>;

export const listFvsInspectionsResponseSchema = z.object({
  items: z.array(fvsInspectionSchema),
  summary: fvsSummarySchema,
});
export type ListFvsInspectionsResponse = z.infer<typeof listFvsInspectionsResponseSchema>;
